import { TableBody, TableHeader } from '@repo/design-system/components/ui/table';
import { Alert, AlertDescription } from '@repo/design-system/components/ui/v2/alert';
import { Badge, type BadgeProps } from '@repo/design-system/components/ui/v2/badge';
import { Card } from '@repo/design-system/components/ui/v2/card';
import { EmptyState as LeveEmptyState } from '@repo/design-system/components/ui/v2/empty-states';
import { Cell, HeaderCell, HeaderRow, Row } from '@repo/design-system/components/ui/v2/cell-row';
import {
  SummaryStrip,
  SummaryStripItem,
  SummaryStripLabel,
  SummaryStripValue,
} from '@repo/design-system/components/ui/v2/summary-strip';
import { TablePanel } from '@repo/design-system/components/ui/v2/table-panel';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Landmark, Loader2, Unplug } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type LeveOrganizationView } from '../../lib/client.ts';
import { cn } from '../../lib/utils.ts';
import { EmptyState } from '../ui/empty-state.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs.tsx';

/**
 * The project's Leve tab: the client's Leve "Payments" and "Fees & Billing", read-only, read from
 * Leve's database by `GET /projects/{id}/leve`. Labels AND markup follow Leve's own organisation
 * page (Remote-Leverage/hriq `org-detail.tsx`, `billing-tab.tsx`, `seats-panel.tsx`,
 * `deposit-panel.tsx`) with the same v2 design-system components (Summary Strip, Table Panel,
 * Cell Row, Badge, Card, Alert), so an account manager reads the same page in both places, minus
 * Leve's actions. Every change is made in Leve ("Open in Leve").
 */

type Agreement = LeveOrganizationView['billing']['agreements'][number];
type SeatDeposit = LeveOrganizationView['billing']['deposits']['seats'][number];
type BadgeColor = NonNullable<BadgeProps['color']>;

const MONEY = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const money = (cents: number) => MONEY.format(cents / 100);
const DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
  timeZone: 'UTC',
});
/** Leve renders its wall-clock dates in UTC (billing-tab.tsx `fmtDate`); so do we. */
const day = (iso: string | null) => (iso ? DATE.format(new Date(iso)) : '');
const amount = (value: string, currency = 'USD') => {
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toLocaleString('en-US')} ${currency}` : `${value} ${currency}`;
};

/* Leve's colours (org-detail.tsx STATUS_BADGE_COLORS, billing-tab.tsx STATUS_COLORS). */
const PAYMENT_STATUS: Record<string, BadgeColor> = {
  completed: 'green',
  pending: 'orange',
  processing: 'blue',
  failed: 'red',
};
const AGREEMENT_STATUS: Record<string, BadgeColor> = {
  active: 'green',
  paused: 'orange',
  cancelled: 'red',
};
const FEE_LABELS: Record<string, string> = { per_contractor: 'per contractor', flat: 'flat fee' };
const BILLING_CYCLE: Record<string, string> = {
  per_pay_run: 'Per pay run',
  '4_weekly': 'Every 4 weeks',
  monthly: 'Monthly',
  yearly: 'Yearly',
};
const cycle = (c: string) => BILLING_CYCLE[c] ?? 'Per pay run';
const fee = (a: Agreement) =>
  a.feeType === 'percentage'
    ? `${a.feeAmount}%`
    : `$${Number(a.feeAmount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
const SEAT_STATE: Record<SeatDeposit['state'], { text: string; color: BadgeColor }> = {
  covered: { text: 'Covered', color: 'green' },
  vacant: { text: 'Empty seat', color: 'gray' },
  waived: { text: 'Waived', color: 'gray' },
  never_collected: { text: 'No deposit', color: 'red' },
  needs_reup: { text: 'Needs re-up', color: 'orange' },
  unpriced: { text: 'No rate on file', color: 'orange' },
};

/* Leve's classes for this markup (org-detail.tsx, billing-tab.tsx, deposit-panel.tsx). */
const SUMMARY_STRIP_CLASS = 'border border-border';
const SUMMARY_STRIP_ITEM_CLASS = 'dark:shadow-[-1px_0_0_0_var(--border),0_-1px_0_0_var(--border)]';
const SUMMARY_STRIP_LABEL_CLASS = 'dark:text-muted-foreground';
const SUMMARY_STRIP_VALUE_CLASS = 'dark:text-foreground';
const V2_PANEL = 'gap-0 border-border p-5 focus-within:shadow-none hover:bg-card';
const V2_HISTORY_PANEL = 'gap-0 border-border p-6 focus-within:shadow-none hover:bg-card';
const V2_ACTIVE_PANEL =
  'gap-0 border-(--v2-alert-success-border) bg-(--v2-alert-success-bg) p-6 focus-within:shadow-none hover:bg-(--v2-alert-success-bg)';
const V2_TRAY =
  'rounded-[16px] bg-linear-to-b from-(--v2-table-panel-bg-from) from-[8.936%] to-(--v2-table-panel-bg-to)';
const V2_NUM_HEAD = 'text-right [&>span]:justify-end';

export function ProjectLeve({
  projectId,
  holdHeight = 0,
}: {
  projectId: string;
  /** While reading, keep this height (the tab left behind) so the page does not jump up. */
  holdHeight?: number;
}) {
  const leve = useQuery({
    queryKey: ['leve', projectId],
    queryFn: () => api.leve.project(projectId),
  });
  if (leve.isPending)
    return (
      <p
        className="text-muted-foreground flex items-start gap-2 text-sm"
        style={holdHeight > 0 ? { minHeight: holdHeight } : undefined}
        data-testid="leve-loading"
      >
        <Loader2 className="size-4 animate-spin" aria-hidden /> Reading Leve…
      </p>
    );
  if (leve.isError)
    return (
      <EmptyState
        icon={Unplug}
        title="Leve could not be read"
        description={describeError(leve.error)}
      />
    );

  const data = leve.data;
  if (data.status !== 'ready')
    return (
      <EmptyState
        icon={Unplug}
        data-testid="leve-empty"
        title={
          data.status === 'not_configured' ? 'Leve is not set up here' : 'Leve is not answering'
        }
        description={data.reason}
      />
    );
  if (data.contactsWithSlug === 0)
    return (
      <EmptyState
        icon={Landmark}
        data-testid="leve-empty"
        title="No contact linked to RecruitCRM"
        description={
          data.contactsWithoutSlug > 0
            ? `Leve finds a client by its RecruitCRM contact. None of this project's ${data.contactsWithoutSlug} contact${data.contactsWithoutSlug === 1 ? '' : 's'} is linked to RecruitCRM yet.`
            : 'Leve finds a client by its RecruitCRM contact, and this project has no contacts.'
        }
      />
    );
  if (data.organizations.length === 0)
    return (
      <EmptyState
        icon={Landmark}
        data-testid="leve-empty"
        title="Not a Leve client"
        description="No Leve organization is linked to this project's RecruitCRM contacts."
      />
    );
  return (
    <div className="flex flex-col gap-6">
      {data.organizations.map((org) => (
        <LeveOrganization key={org.id} org={org} />
      ))}
    </div>
  );
}

function LeveOrganization({ org }: { org: LeveOrganizationView }) {
  const [tab, setTab] = useState<'payments' | 'billing'>('payments');
  return (
    <section aria-label={`Leve: ${org.name}`} className="ds-v2 flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-semibold">{org.name}</h3>
          <p className="text-muted-foreground text-xs">
            Linked through {org.matchedContacts.map((c) => c.name).join(', ')}
          </p>
        </div>
        {org.url && (
          <a
            href={org.url}
            target="_blank"
            rel="noreferrer"
            className="text-primary inline-flex items-center gap-1 text-sm hover:underline"
          >
            Open in Leve <ExternalLink className="size-3.5" aria-hidden />
          </a>
        )}
      </div>
      <Tabs value={tab} onValueChange={(v) => setTab(v as 'payments' | 'billing')}>
        <TabsList>
          <TabsTrigger className="px-1.5 pt-2 pb-2.5 text-sm" value="payments">
            Payments ({org.payments.count})
          </TabsTrigger>
          <TabsTrigger className="px-1.5 pt-2 pb-2.5 text-sm" value="billing">
            Fees &amp; Billing
          </TabsTrigger>
        </TabsList>
        <TabsContent value="payments" className="mt-4">
          <Payments org={org} />
        </TabsContent>
        <TabsContent value="billing" className="mt-4 space-y-6">
          <Agreements org={org} />
          <Seats org={org} />
          <Deposits org={org} />
        </TabsContent>
      </Tabs>
    </section>
  );
}

/** Leve's Summary Strip with its org-detail.tsx classes (shared with the header's KPI strip). */
export function Strip({
  items,
  className,
}: {
  items: { label: string; value: string; className?: string }[];
  className?: string;
}) {
  return (
    <SummaryStrip className={cn(SUMMARY_STRIP_CLASS, className)}>
      {items.map((i) => (
        <SummaryStripItem className={SUMMARY_STRIP_ITEM_CLASS} key={i.label}>
          <SummaryStripLabel className={SUMMARY_STRIP_LABEL_CLASS}>{i.label}</SummaryStripLabel>
          <SummaryStripValue className={i.className ?? SUMMARY_STRIP_VALUE_CLASS}>
            {i.value}
          </SummaryStripValue>
        </SummaryStripItem>
      ))}
    </SummaryStrip>
  );
}

function Head({ cells }: { cells: { label: string; className?: string }[] }) {
  return (
    <TableHeader>
      <HeaderRow>
        {cells.map((c, i) => (
          <HeaderCell
            key={c.label}
            className={cn(i === 0 && 'pl-4', i === cells.length - 1 && 'pr-4', c.className)}
            sortable={false}
          >
            {c.label}
          </HeaderCell>
        ))}
      </HeaderRow>
    </TableHeader>
  );
}

function Payments({ org }: { org: LeveOrganizationView }) {
  const p = org.payments;
  return (
    <div className="space-y-4">
      <Strip
        items={[
          {
            label: 'Completed',
            value: `$${Number(p.completedTotal).toLocaleString('en-US')}`,
            className: 'text-(--v2-badge-green-text)',
          },
          {
            label: 'Pending',
            value: `$${Number(p.pendingTotal).toLocaleString('en-US')}`,
            className: 'text-(--v2-badge-orange-text)',
          },
        ]}
      />
      {p.items.length === 0 ? (
        <p className="text-muted-foreground text-sm">No payments in Leve yet.</p>
      ) : (
        <>
          <TablePanel>
            <Head
              cells={['Contractor', 'Type', 'Amount', 'Method', 'Period', 'Status'].map(
                (label) => ({ label }),
              )}
            />
            <TableBody>
              {p.items.map((x) => (
                <Row key={x.id}>
                  <Cell className="pl-4">
                    <span className="font-medium">{x.contractorName}</span>
                  </Cell>
                  <Cell>
                    <Badge className="capitalize" color="darkGray" size="sm">
                      {x.paymentType}
                    </Badge>
                  </Cell>
                  <Cell>
                    <span className="font-medium whitespace-nowrap">
                      {amount(x.amount, x.currency)}
                    </span>
                  </Cell>
                  <Cell>
                    <span className="text-muted-foreground text-xs capitalize">
                      {x.paymentMethod?.replace(/_/g, ' ') ?? '—'}
                    </span>
                  </Cell>
                  <Cell>
                    <span className="text-muted-foreground text-xs whitespace-nowrap">
                      {x.periodStart
                        ? `${day(x.periodStart)} – ${day(x.periodEnd)}`
                        : day(x.createdAt)}
                    </span>
                  </Cell>
                  <Cell className="pr-4">
                    <Badge
                      className="capitalize"
                      color={PAYMENT_STATUS[x.status] ?? 'gray'}
                      size="sm"
                    >
                      {x.status}
                    </Badge>
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </TablePanel>
          {p.count > p.items.length && (
            <p className="text-muted-foreground text-xs">
              The newest {p.items.length} of {p.count} payments. The totals count all of them.
            </p>
          )}
        </>
      )}
    </div>
  );
}

function Agreements({ org }: { org: LeveOrganizationView }) {
  const list = org.billing.agreements;
  const active = list.find((a) => a.id === org.billing.activeAgreementId);
  return (
    <div className="space-y-4">
      {active ? (
        <Card className={V2_ACTIVE_PANEL} data-testid="leve-active-agreement">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold">Active Fee Agreement</h3>
            <Badge color="green" size="sm">
              Active
            </Badge>
          </div>
          <div className="mt-2 text-2xl font-bold">
            {fee(active)}
            <span className="text-muted-foreground ml-1 text-sm font-normal">
              {FEE_LABELS[active.feeType]}
            </span>
          </div>
          <p className="text-muted-foreground mt-1 text-xs">
            Billed {cycle(active.billingCycle).toLowerCase()} · Since {day(active.startDate)}
            {active.freeTrialDays > 0 && ` · ${active.freeTrialDays}-day free trial`}
          </p>
          {active.notes && <p className="text-muted-foreground mt-2 text-sm">{active.notes}</p>}
        </Card>
      ) : (
        <LeveEmptyState
          title="No active fee agreement for this organization."
          description="Fee agreements are set up by the billing team, in Leve."
        />
      )}
      {list.length > 0 && (
        <Card className={V2_HISTORY_PANEL}>
          <h3 className="font-semibold">Agreement History</h3>
          <TablePanel className="mt-4">
            <Head
              cells={[
                { label: 'Name' },
                { label: 'Fee' },
                { label: 'Cycle' },
                { label: 'Status', className: 'text-center [&>span]:justify-center' },
                { label: 'Since' },
              ]}
            />
            <TableBody>
              {list.map((a) => (
                <Row key={a.id}>
                  <Cell className="pl-4">
                    <span className="font-medium">{a.name}</span>
                  </Cell>
                  <Cell className="tabular-nums whitespace-nowrap">
                    {a.feeType === 'percentage'
                      ? `${a.feeAmount}%`
                      : `$${Number(a.feeAmount).toFixed(2)}`}
                    <span className="text-muted-foreground ml-1 text-xs">
                      {FEE_LABELS[a.feeType]}
                    </span>
                  </Cell>
                  <Cell>
                    <span className="text-muted-foreground">{cycle(a.billingCycle)}</span>
                  </Cell>
                  <Cell className="text-center">
                    <Badge color={AGREEMENT_STATUS[a.status] ?? 'gray'} size="sm">
                      {a.status}
                    </Badge>
                  </Cell>
                  <Cell className="pr-4">
                    <span className="text-muted-foreground">{day(a.startDate)}</span>
                    {a.status === 'cancelled' && a.endDate && (
                      <span
                        className="text-muted-foreground block max-w-xs truncate text-xs"
                        title={a.cancelReason ?? undefined}
                      >
                        Ended {day(a.endDate)}
                        {a.cancelReason ? ` · ${a.cancelReason}` : ''}
                      </span>
                    )}
                  </Cell>
                </Row>
              ))}
            </TableBody>
          </TablePanel>
        </Card>
      )}
    </div>
  );
}

function Seats({ org }: { org: LeveOrganizationView }) {
  const { items, free } = org.billing.seats;
  return (
    <Card className={V2_PANEL}>
      <div>
        <h3 className="text-sm font-semibold">Seats</h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          Slots this client pays for. A seat bills whether or not someone is in it — emptying one
          doesn&apos;t stop the charge, cancelling does.
        </p>
      </div>
      {free < 0 && (
        <Alert className="mt-3" variant="warning">
          <AlertDescription>
            More contractors than seats — this client is being billed for people who have no seat (
            {Math.abs(free)} short).
          </AlertDescription>
        </Alert>
      )}
      {free > 0 && (
        <p className="text-muted-foreground mt-3 text-xs">
          {free} seat{free === 1 ? '' : 's'} free — the client is paying for{' '}
          {free === 1 ? 'it' : 'them'} whether or not anyone is in {free === 1 ? 'it' : 'them'}.
        </p>
      )}
      {items.length === 0 ? (
        <p className="text-muted-foreground mt-3 text-xs">No seats yet.</p>
      ) : (
        <TablePanel className="mt-4">
          <Head cells={['Seat', 'Contractor', 'Deposit', 'Since'].map((label) => ({ label }))} />
          <TableBody>
            {items.map((s) => (
              <Row key={s.id}>
                <Cell className="pl-4 font-medium whitespace-nowrap">Seat {s.number}</Cell>
                <Cell>
                  <span className="font-medium">
                    {s.occupantName
                      ? `${s.occupantName}${s.occupantCurrent === false ? ' (no longer active)' : ''}`
                      : 'Empty seat'}
                  </span>
                </Cell>
                <Cell>
                  {!s.depositWaived &&
                  s.depositHeldCents === 0 &&
                  s.depositInvoicedUnpaidCents === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <span className="flex flex-col text-xs">
                      {s.depositWaived && <span>Waived</span>}
                      {s.depositHeldCents !== 0 && <span>{money(s.depositHeldCents)} held</span>}
                      {s.depositInvoicedUnpaidCents !== 0 && (
                        <span className="text-amber-700 dark:text-amber-400">
                          {money(s.depositInvoicedUnpaidCents)}{' '}
                          {s.depositWaived ? 'still invoiced' : 'invoiced, unpaid'}
                        </span>
                      )}
                    </span>
                  )}
                </Cell>
                {/* Leve's own format here: the viewer's locale (seats-panel.tsx), on the UTC day. */}
                <Cell className="pr-4 whitespace-nowrap">
                  {new Date(s.startDate).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                </Cell>
              </Row>
            ))}
          </TableBody>
        </TablePanel>
      )}
    </Card>
  );
}

function Deposits({ org }: { org: LeveOrganizationView }) {
  const d = org.billing.deposits;
  const offSeat = d.heldOffSeatCents - d.unassignedCents;
  return (
    <Card className={V2_PANEL}>
      <div>
        <h3 className="text-sm font-semibold">Contractor deposits</h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          One month of each contractor&apos;s pay, held against their seat. It can cover their final
          month — if the seat is refilled after that, the deposit needs topping back up.
        </p>
      </div>
      <Strip
        className="mt-4"
        items={[
          { label: 'Required', value: money(d.requiredCents), className: 'text-2xl tabular-nums' },
          { label: 'Held', value: money(d.heldCents), className: 'text-2xl tabular-nums' },
          {
            label: d.shortfallCents === 0 ? 'Covered' : 'Short by',
            value: d.shortfallCents === 0 ? '✓' : money(d.shortfallCents),
            className: cn(
              'text-2xl tabular-nums',
              d.shortfallCents === 0
                ? 'text-(--v2-summary-strip-trend-positive)'
                : 'text-(--v2-summary-strip-trend-negative)',
            ),
          },
        ]}
      />
      <div className={cn('mt-3 flex flex-wrap items-center gap-2 px-3 py-2 text-xs', V2_TRAY)}>
        <span className="text-muted-foreground">Agreed per seat</span>
        <span className="font-medium tabular-nums">
          {d.agreedPerSeatCents > 0 ? money(d.agreedPerSeatCents) : 'none'}
        </span>
        <span className="text-muted-foreground">
          A floor. Required rises above it when a contractor&apos;s month costs more.
        </span>
      </div>
      {d.unassignedCents > 0 && (
        <Alert className="mt-3" variant="info">
          <AlertDescription>
            {money(d.unassignedCents)} extra deposited by the client, not on any seat yet.
          </AlertDescription>
        </Alert>
      )}
      {offSeat !== 0 && (
        <Alert className="mt-3" variant="warning">
          <AlertDescription>
            {money(offSeat)} held against no active seat — cancelled seats or unattached entries.
          </AlertDescription>
        </Alert>
      )}
      {d.seats.length === 0 ? (
        <p className="text-muted-foreground mt-4 text-xs">No active seats for this client yet.</p>
      ) : (
        <TablePanel className="mt-4" tableClassName="min-w-[46rem]">
          <Head
            cells={[
              { label: 'Seat' },
              { label: 'Required', className: V2_NUM_HEAD },
              { label: 'Held', className: V2_NUM_HEAD },
              { label: 'Short', className: V2_NUM_HEAD },
              { label: 'Status' },
            ]}
          />
          <TableBody>
            {d.seats.map((s) => {
              const label = SEAT_STATE[s.state];
              return (
                <Row key={s.seatId}>
                  <Cell className="pl-4">
                    <span className="font-medium">{s.occupantName ?? 'Empty seat'}</span>
                  </Cell>
                  <Cell className="text-right tabular-nums">{money(s.requiredCents)}</Cell>
                  <Cell className="text-right tabular-nums">
                    <span className="text-muted-foreground">{money(s.heldCents)}</span>
                  </Cell>
                  <Cell className="text-right tabular-nums">
                    <span
                      className={
                        s.shortfallCents > 0
                          ? 'text-destructive font-medium'
                          : 'text-muted-foreground'
                      }
                    >
                      {s.shortfallCents === 0 ? '—' : money(s.shortfallCents)}
                    </span>
                  </Cell>
                  <Cell className="pr-4 whitespace-nowrap">
                    <Badge color={label.color} size="sm">
                      {label.text}
                    </Badge>
                    {s.openDepositInvoice && (
                      <span className="text-muted-foreground block text-xs">
                        Billed on {s.openDepositInvoice.invoiceNumber} (
                        {s.openDepositInvoice.status})
                      </span>
                    )}
                  </Cell>
                </Row>
              );
            })}
          </TableBody>
        </TablePanel>
      )}
      {d.entries.length > 0 && (
        <TablePanel className="mt-4" tableClassName="min-w-[46rem]">
          <Head
            cells={[
              { label: 'Date' },
              { label: 'Seat' },
              { label: 'Movement' },
              { label: 'Note' },
              { label: 'By' },
              { label: 'Amount', className: V2_NUM_HEAD },
            ]}
          />
          <TableBody>
            {d.entries.map((e) => (
              <Row key={e.id}>
                <Cell className="pl-4 whitespace-nowrap">
                  <span className="text-muted-foreground">{day(e.createdAt)}</span>
                </Cell>
                <Cell>{e.seatOccupant ?? '—'}</Cell>
                <Cell className="capitalize">{e.kind}</Cell>
                <Cell className="max-w-[18rem] truncate" title={e.note}>
                  <span className="text-muted-foreground">{e.note}</span>
                </Cell>
                <Cell>
                  <span className="text-muted-foreground">{e.createdByName ?? '—'}</span>
                </Cell>
                <Cell className="pr-4 text-right tabular-nums">
                  <span className={Number(e.amount) < 0 ? 'text-muted-foreground' : 'font-medium'}>
                    {money(Math.round(Number(e.amount) * 100))}
                  </span>
                </Cell>
              </Row>
            ))}
          </TableBody>
        </TablePanel>
      )}
    </Card>
  );
}
